import { Router } from '@angular/router';
import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule, ModalController } from '@ionic/angular';
import { DocumentData } from 'firebase/firestore';
import { BehaviorSubject, Observable, combineLatest, of } from 'rxjs';
import { delay, take, tap } from 'rxjs/operators';
import { LoadingService, SnackbarService } from '@smartflip/ui-utils';
import { propertyCategories } from '@smartflip/data-constants';
import { CategorizedProperties, Property } from '@smartflip/data-models';
import { LoadingComponent } from '@smartflip/loading-indicator';
import {
  CustomerService,
  PropertyService,
  UserService,
  createPropertyList,
} from '@smartflip/helper-utils';
import { AddPropertyModalComponent } from './add-property-modal/add-property-modal.component';
import {
  defaultButtonStates,
  downloadErrorMessage,
  homeListButtons,
  subscriptionWarningTexts,
} from './home-list.constants';
import { downloadSummaryPDF, setButtonStates } from './home-list.helper';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { ButtonStates, HomeListButtonActionType } from './home-list.models';
import { HomeListSubscriptionBannerComponent } from './subscription-banner/home-list-subscription-banner.component';
import { FinancialAggregatesComponent } from './financial-aggregates/financial-aggregates.component';
import { HomeListCardComponent } from './home-list-card/home-list-card.component';

@Component({
  selector: 'app-home-list',
  templateUrl: './home-list.component.html',
  styleUrls: ['./home-list.component.scss'],
  imports: [
    CommonModule,
    IonicModule,
    HomeListSubscriptionBannerComponent,
    FinancialAggregatesComponent,
    LoadingComponent,
    HomeListCardComponent,
  ],
})
export class HomeListComponent implements OnInit {
  properties$: Observable<CategorizedProperties>;
  public loading = true;
  public errorMessage = '';
  public cats = propertyCategories;
  public buttonList = homeListButtons;
  public subscriptionWarningTexts = subscriptionWarningTexts;
  public buttonStates: BehaviorSubject<ButtonStates> = new BehaviorSubject(
    defaultButtonStates,
  );
  public buttonStates$ = this.buttonStates.asObservable();
  public isPremiumUser: boolean = false;

  constructor(
    private router: Router,
    private modalController: ModalController,
    private http: HttpClient,
    private propService: PropertyService,
    private customerService: CustomerService,
    private loadingService: LoadingService,
    private snackbarService: SnackbarService,
    private userService: UserService,
  ) {}

  public noProperties(propertyList: Property[]): boolean {
    return propertyList.length === 0;
  }

  public handleButtonClick(actionType: HomeListButtonActionType) {
    const actionMap = {
      newProperty: this.newProperty,
      downloadSummary: this.downloadSummary,
      exportData: this.exportData,
    };

    actionMap[actionType].call(this);
  }

  private async newProperty() {
    const modal = await this.modalController.create({
      component: AddPropertyModalComponent,
    });

    await modal.present();

    const { data: newPropertyData } = await modal.onWillDismiss();
    if (newPropertyData) {
      this.addNewProperty(newPropertyData);
    }
  }

  private exportData() {
    this.router.navigate(['/home/export']);
  }

  private downloadSummary() {
    // TODO: Connect to a button and replace with env vars for prod and dev environments
    //  consider keeping one for local testing as well as seen below
    this.loading = true;
    downloadSummaryPDF(this.http, this.userService)
      .pipe(take(1))
      .subscribe((downloadState) => {
        if (downloadState instanceof HttpErrorResponse) {
          const fourSeconds = 4000;

          this.snackbarService.presentToast(
            'top',
            downloadErrorMessage,
            fourSeconds,
            null,
            'error-toaster',
          );
        }

        this.loading = false;
      });
  }

  private async addNewProperty(newPropertyData) {
    const loader = await this.loadingService.showLoading();

    this.propService
      .addProperty(newPropertyData)
      .then((newPropertyId: string) => {
        this.propService.updateProperty('gen', newPropertyData, newPropertyId);
        this.router.navigate(['/home', newPropertyId]);
      })
      .finally(() => loader.dismiss());
  }

  private limitViewByPlan(): Observable<[boolean, DocumentData[]]> {
    return combineLatest([
      this.customerService.hasActiveSubscription$,
      this.propService.properties$,
    ]).pipe(
      delay(600),
      tap(([hasActiveSubscription, properties]) => {
        const updatedProperties = createPropertyList(
          properties as Property[],
          hasActiveSubscription,
        );
        const updatedButtonStates = setButtonStates(
          updatedProperties,
          hasActiveSubscription,
        );

        this.isPremiumUser = !hasActiveSubscription;
        this.properties$ = of(updatedProperties);
        this.buttonStates.next(updatedButtonStates);
      }),
    );
  }

  ngOnInit(): void {
    this.limitViewByPlan().subscribe(([hasActiveSubscription, properties]) => {
      this.loading = typeof hasActiveSubscription !== 'boolean' && !!properties;
    });
  }
}
